import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Checkbox,
    Grid,
    Typography,
    CircularProgress,
    Chip,
    IconButton,
    Divider,
    Tooltip,
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CustomizedSnackbars from '../material-snackbars';
import {
    discoverWebhookResponsePaths,
    getWebhookLastResponses,
    createWebhookResponseMapping,
} from '../../services/webhook.service';

// Cor por tipo sugerido (visual quick-scan).
const TYPE_COLORS = {
    id: '#1976d2',
    key: '#7b1fa2',
    url: '#388e3c',
    reference: '#f57c00',
    generic: '#757575',
};

// Dialog de auto-discovery: 3 fluxos pra obter response body de amostra:
//   1) Ultimo delivery bem-sucedido do webhook (default)
//   2) Selecionar delivery especifico da lista (dropdown)
//   3) Colar JSON manualmente
// Depois de descobrir, cliente marca checkboxes e envia — cria mappings em batch.
class WebhookDiscoverPathsDialog extends Component {
    constructor(props) {
        super(props);
        this.state = this.buildInitialState();
        this.handleDiscover = this.handleDiscover.bind(this);
        this.handleTogglePath = this.handleTogglePath.bind(this);
        this.handleUpdatePathField = this.handleUpdatePathField.bind(this);
        this.handleCreateSelected = this.handleCreateSelected.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleSourceChange = this.handleSourceChange.bind(this);
    }

    buildInitialState() {
        return {
            source: 'last', // 'last' | 'specific' | 'manual'
            manualBody: '',
            selectedDeliveryId: null,
            lastResponses: [],
            loadingLast: false,
            discovering: false,
            creating: false,
            discoveredPaths: [],
            responseBodyPreview: '',
            sourceDeliveryLogId: null,
            sourceDeliveryDate: null,
            selectedPaths: {}, // { jsonpath: { checked, keyName, entityType, description } }
            notification: { open: false, variant: 'success', message: '' },
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.setState(this.buildInitialState(), () => {
                // Ao abrir, pre-carrega ultimas execucoes pro dropdown + faz discovery com o "last".
                this.loadLastResponses();
                this.handleDiscover();
            });
        }
    }

    async loadLastResponses() {
        this.setState({ loadingLast: true });
        try {
            const res = await getWebhookLastResponses(this.props.webhookId, 5);
            if (res.success && Array.isArray(res.data)) {
                this.setState({ lastResponses: res.data });
            }
        } finally {
            this.setState({ loadingLast: false });
        }
    }

    handleSourceChange(newSource) {
        this.setState({ source: newSource, discoveredPaths: [], selectedPaths: {}, responseBodyPreview: '' });
    }

    async handleDiscover() {
        const { source, manualBody, selectedDeliveryId } = this.state;
        const { webhookId, intl } = this.props;

        if (source === 'manual' && !manualBody.trim()) {
            this.showNotification('error', intl.formatMessage({ id: 'webhook.response.discover.no.manual.body' }));
            return;
        }
        if (source === 'specific' && !selectedDeliveryId) {
            this.showNotification('error', intl.formatMessage({ id: 'webhook.response.discover.no.delivery' }));
            return;
        }

        this.setState({ discovering: true, discoveredPaths: [], selectedPaths: {} });
        try {
            const payload = {};
            if (source === 'manual') payload.responseBody = manualBody;
            if (source === 'specific') payload.deliveryLogId = selectedDeliveryId;

            const res = await discoverWebhookResponsePaths(webhookId, payload);

            if (!res.success || !res.data) {
                this.showNotification('error', intl.formatMessage({ id: 'webhook.response.discover.error' }));
                return;
            }
            const d = res.data;
            if (d.source === 'none' || !d.paths || d.paths.length === 0) {
                this.showNotification('warning', intl.formatMessage({ id: 'webhook.response.discover.no.paths' }));
                this.setState({ discoveredPaths: [], responseBodyPreview: d.responsebodypreview || '' });
                return;
            }

            // Estado inicial: nenhum path marcado — usuario decide.
            const selectedPaths = {};
            d.paths.forEach(p => {
                selectedPaths[p.jsonpath] = {
                    checked: false,
                    keyName: p.suggestedkeyname || '',
                    entityType: p.suggestedtype || 'generic',
                    description: '',
                };
            });

            this.setState({
                discoveredPaths: d.paths,
                responseBodyPreview: d.responsebodypreview || '',
                sourceDeliveryLogId: d.sourcedeliverylogid || null,
                sourceDeliveryDate: d.sourcedeliverydate || null,
                selectedPaths,
            });
        } catch (err) {
            this.showNotification('error', err.message);
        } finally {
            this.setState({ discovering: false });
        }
    }

    handleTogglePath(jsonpath, checked) {
        this.setState(prev => ({
            selectedPaths: {
                ...prev.selectedPaths,
                [jsonpath]: { ...prev.selectedPaths[jsonpath], checked }
            }
        }));
    }

    handleUpdatePathField(jsonpath, field, value) {
        this.setState(prev => ({
            selectedPaths: {
                ...prev.selectedPaths,
                [jsonpath]: { ...prev.selectedPaths[jsonpath], [field]: value }
            }
        }));
    }

    async handleCreateSelected() {
        const { selectedPaths } = this.state;
        const { webhookId, intl, onCreated } = this.props;
        const toCreate = Object.entries(selectedPaths)
            .filter(([, v]) => v.checked)
            .map(([jsonpath, v]) => ({ jsonpath, ...v }));

        if (toCreate.length === 0) {
            this.showNotification('warning', intl.formatMessage({ id: 'webhook.response.discover.no.selection' }));
            return;
        }
        // Valida antes de disparar as N chamadas.
        for (const item of toCreate) {
            if (!item.keyName.trim() || !item.entityType.trim()) {
                this.showNotification('error', intl.formatMessage({ id: 'webhook.response.discover.incomplete' }));
                return;
            }
        }

        this.setState({ creating: true });
        try {
            let successCount = 0;
            let errorCount = 0;
            for (const item of toCreate) {
                const res = await createWebhookResponseMapping(webhookId, {
                    appConnectorProviderId: null,
                    jsonPath: item.jsonpath,
                    externalKeyName: item.keyName.trim(),
                    externalEntityType: item.entityType.trim(),
                    description: item.description?.trim() || null,
                    active: true,
                });
                if (res.success) successCount++; else errorCount++;
            }

            if (errorCount === 0) {
                this.showNotification('success', intl.formatMessage({ id: 'webhook.response.discover.created' }, { count: successCount }));
                if (onCreated) onCreated();
                this.handleClose();
            } else {
                this.showNotification('warning', intl.formatMessage({ id: 'webhook.response.discover.partial' }, { ok: successCount, err: errorCount }));
                if (successCount > 0 && onCreated) onCreated();
            }
        } catch (err) {
            this.showNotification('error', err.message);
        } finally {
            this.setState({ creating: false });
        }
    }

    handleClose() {
        if (this.state.creating || this.state.discovering) return; // evita fechar no meio de operacao
        if (this.props.onClose) this.props.onClose();
    }

    showNotification(variant, message) {
        this.setState({ notification: { open: true, variant, message } });
    }

    closeNotification = () => this.setState(prev => ({ notification: { ...prev.notification, open: false } }));

    render() {
        const { intl, open } = this.props;
        const {
            source, manualBody, selectedDeliveryId,
            lastResponses, loadingLast,
            discovering, creating, discoveredPaths, responseBodyPreview,
            sourceDeliveryLogId, sourceDeliveryDate, selectedPaths, notification,
        } = this.state;

        const selectedCount = Object.values(selectedPaths).filter(p => p.checked).length;

        return (
            <>
                <Dialog
                    open={open}
                    onClose={this.handleClose}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 2 } }}
                >
                    <DialogTitle>
                        {intl.formatMessage({ id: 'webhook.response.discover.title' })}
                    </DialogTitle>
                    <DialogContent dividers sx={{ p: 0 }}>
                        <div style={{ maxHeight: '75vh', overflowY: 'auto', padding: '16px 20px' }}>
                            <Grid container spacing={2}>
                                {/* Selecao da fonte do JSON */}
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        {intl.formatMessage({ id: 'webhook.response.discover.help' })}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <FormControl variant="outlined" fullWidth size="small">
                                        <InputLabel shrink>{intl.formatMessage({ id: 'webhook.response.discover.source' })}</InputLabel>
                                        <Select
                                            value={source}
                                            onChange={(e) => this.handleSourceChange(e.target.value)}
                                            label={intl.formatMessage({ id: 'webhook.response.discover.source' })}
                                            displayEmpty
                                        >
                                            <MenuItem value="last">{intl.formatMessage({ id: 'webhook.response.discover.source.last' })}</MenuItem>
                                            <MenuItem value="specific">{intl.formatMessage({ id: 'webhook.response.discover.source.specific' })}</MenuItem>
                                            <MenuItem value="manual">{intl.formatMessage({ id: 'webhook.response.discover.source.manual' })}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {source === 'specific' && (
                                    <Grid item xs={12}>
                                        <FormControl variant="outlined" fullWidth size="small">
                                            <InputLabel shrink>{intl.formatMessage({ id: 'webhook.response.discover.pick.delivery' })}</InputLabel>
                                            <Select
                                                value={selectedDeliveryId || ''}
                                                onChange={(e) => this.setState({ selectedDeliveryId: e.target.value })}
                                                label={intl.formatMessage({ id: 'webhook.response.discover.pick.delivery' })}
                                                displayEmpty
                                            >
                                                <MenuItem value=""><em>{intl.formatMessage({ id: 'select' })}...</em></MenuItem>
                                                {loadingLast && <MenuItem disabled><CircularProgress size={14} /> {intl.formatMessage({ id: 'loading' })}</MenuItem>}
                                                {lastResponses.map(r => (
                                                    <MenuItem key={r.deliverylogid} value={r.deliverylogid}>
                                                        #{r.deliverylogid} — {moment(r.sentat).format('DD/MM/YYYY HH:mm')} — HTTP {r.statuscode || '?'}
                                                    </MenuItem>
                                                ))}
                                                {!loadingLast && lastResponses.length === 0 && (
                                                    <MenuItem disabled>{intl.formatMessage({ id: 'webhook.response.discover.no.deliveries' })}</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}

                                {source === 'manual' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            variant="outlined"
                                            fullWidth
                                            multiline
                                            rows={6}
                                            label={intl.formatMessage({ id: 'webhook.response.discover.paste.json' })}
                                            value={manualBody}
                                            onChange={(e) => this.setState({ manualBody: e.target.value })}
                                            placeholder='{ "id": 10706, "key": "SCRUM-20" }'
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={this.handleDiscover}
                                        disabled={discovering}
                                        startIcon={discovering ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                                    >
                                        {intl.formatMessage({ id: 'webhook.response.discover.suggest' })}
                                    </Button>
                                    {sourceDeliveryLogId && (
                                        <Chip
                                            style={{ marginLeft: 12 }}
                                            size="small"
                                            variant="outlined"
                                            label={intl.formatMessage(
                                                { id: 'webhook.response.discover.using.delivery' },
                                                { id: sourceDeliveryLogId, date: sourceDeliveryDate ? moment(sourceDeliveryDate).format('DD/MM/YYYY HH:mm') : '' }
                                            )}
                                        />
                                    )}
                                </Grid>

                                {/* Preview do JSON */}
                                {responseBodyPreview && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
                                            {intl.formatMessage({ id: 'webhook.response.discover.preview' })}
                                        </Typography>
                                        <div style={{
                                            backgroundColor: '#1a1a2e', color: '#e0e0e0',
                                            fontFamily: 'monospace', fontSize: 12, padding: 12, borderRadius: 4,
                                            maxHeight: 200, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                        }}>
                                            {responseBodyPreview}
                                        </div>
                                    </Grid>
                                )}

                                {/* Lista de paths sugeridos */}
                                {discoveredPaths.length > 0 && (
                                    <>
                                        <Grid item xs={12}><Divider /></Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2">
                                                {intl.formatMessage(
                                                    { id: 'webhook.response.discover.paths.header' },
                                                    { count: discoveredPaths.length, selected: selectedCount }
                                                )}
                                            </Typography>
                                        </Grid>
                                        {discoveredPaths.map((p) => {
                                            const state = selectedPaths[p.jsonpath] || {};
                                            return (
                                                <Grid item xs={12} key={p.jsonpath}>
                                                    <Box
                                                        sx={{
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: 1,
                                                            padding: 1.5,
                                                            backgroundColor: state.checked ? 'rgba(139, 195, 74, 0.08)' : '#fff',
                                                        }}
                                                    >
                                                        <Grid container spacing={1} alignItems="flex-start">
                                                            <Grid item>
                                                                <Checkbox
                                                                    checked={!!state.checked}
                                                                    onChange={(e) => this.handleTogglePath(p.jsonpath, e.target.checked)}
                                                                />
                                                            </Grid>
                                                            <Grid item xs>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={12} sm={6}>
                                                                        <Typography variant="body2" style={{ fontFamily: 'monospace', color: '#1976d2' }}>
                                                                            {p.jsonpath}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', wordBreak: 'break-all' }}>
                                                                            = {p.samplevalue}
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={3}>
                                                                        <TextField
                                                                            size="small"
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            label={intl.formatMessage({ id: 'webhook.response.mapping.key' })}
                                                                            value={state.keyName || ''}
                                                                            onChange={(e) => this.handleUpdatePathField(p.jsonpath, 'keyName', e.target.value)}
                                                                            disabled={!state.checked}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={3}>
                                                                        <TextField
                                                                            size="small"
                                                                            variant="outlined"
                                                                            fullWidth
                                                                            label={intl.formatMessage({ id: 'webhook.response.mapping.entitytype' })}
                                                                            value={state.entityType || ''}
                                                                            onChange={(e) => this.handleUpdatePathField(p.jsonpath, 'entityType', e.target.value)}
                                                                            disabled={!state.checked}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item>
                                                                <Chip
                                                                    size="small"
                                                                    label={p.suggestedtype}
                                                                    style={{ backgroundColor: TYPE_COLORS[p.suggestedtype] || '#757575', color: 'white' }}
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </>
                                )}
                            </Grid>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button onClick={this.handleClose} disabled={creating}>
                            {intl.formatMessage({ id: 'cancel' })}
                        </Button>
                        <Button
                            onClick={this.handleCreateSelected}
                            variant="contained"
                            color="primary"
                            disabled={selectedCount === 0 || creating || discovering}
                            startIcon={creating ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                            {intl.formatMessage({ id: 'webhook.response.discover.create.selected' }, { count: selectedCount })}
                        </Button>
                    </DialogActions>
                </Dialog>
                <CustomizedSnackbars
                    variant={notification.variant}
                    message={notification.message}
                    isOpen={notification.open}
                    toClose={this.closeNotification}
                />
            </>
        );
    }
}

WebhookDiscoverPathsDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    webhookId: PropTypes.number.isRequired,
    onCreated: PropTypes.func,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(WebhookDiscoverPathsDialog);
